import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Modal,
  Popover,
  Select,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CustomDialog, {
  CLINICAL_RECORD_DIALOG_WIDTH
} from 'src/components/UI/dialog/CustomDialog';
import RecordBrowserModal from './RecordBrowserModal';
import { Grid, Chip } from '@mui/material';
import TileCard from './NoteTile';
import { getRecordDate } from './forms/formService';

import axios from 'src/axios';

import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm,
  ProcessForm,
  RiskFactorForm
} from './forms/FormsExport';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import moment from 'moment';
import { AppContext, ThemeContext } from 'src/store/ContextStore';
import HistoryIcon from '@mui/icons-material/History';
import { config as reproConfig } from '../../../pedigree-tool/pedigree/config';
import FilterListIcon from '@mui/icons-material/FilterList';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';

import TemplateStore from 'src/store/TemplateStore';
import AcitonDialog from 'src/components/UI/dialog/AcitonDialog';
import { formatPersonSummary } from '../../util';
import { isAnonymousActive } from 'src/components/pedigree-chart/anonymousPedigrees';
import ModalHeader from '../diagnoses-menu/ModalHeader';

function ClinicalNotes({
  data,
  modificationHistoryHandle,
  deepCopy,
  setMemberRecords,
  isDisabled: isLocked
}) {
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { familyId } = user;
  const { theme } = React.useContext(ThemeContext);
  const anonymousActive = isAnonymousActive(content?.settings?.pedigreeSetting);

  let [isModalOpen, setOpenModal] = React.useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = React.useState(false);
  const [sortOrder, setSortOrder] = React.useState('newest');
  const browseAllRef = useRef(null);
  const familyMemberId =
    data?.familyMemberId ?? data?.proBandId ?? data?.pid ?? 'none';

  let [unsavedChanges, setUnsavedChanges] = React.useState({
    status: false,
    edit: false
  });
  let [noteType, setNoteType] = React.useState('Referral');
  let [isEditing, setIsEditing] = React.useState(false);
  let [currentModel, setCurrentModel] = React.useState({});
  const [staffMembers, setStaffMembers] = useState([]);

  const [filter, setFilter] = React.useState({
    Referral: true,
    Appointment: true,
    Contact: true,
    Consent: true,
    Sample: true,
    Test: true,
    Treatment: true,
    Address: true,
    'Clinical Note': true,
    'Tissue Pathology': true,
    Process: true,
    'Risk Score': true
  });

  let [tiles, setTiles] = React.useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const familyMemberIdFromURL = queryParams.get('familyMemberId');
  const appointmentIdFromURL = queryParams.get('appointmentId');
  const referralIdFromURL = queryParams.get('referralId');
  const contactIdFromURL = queryParams.get('contactId');
  const sampleIdFromURL = queryParams.get('sampleId');
  const testIdFromURL = queryParams.get('testId');
  const clinicalNoteIdFromURL = queryParams.get('clinicalNoteId');
  const consentIdFromURL = queryParams.get('consentId');
  const isOpenedModal = useRef(false);

  let [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      const store = TemplateStore.getInstance();
      await store.fetchTemplates();
    };
    loadTemplates();
  }, []);

  const staffMemberOptions = React.useMemo(() => {
    return staffMembers.map((d) => ({
      label: d.firstName + ' ' + d.lastName,
      value: d.userId
    }));
  }, [staffMembers]);

  const memberTiles = React.useMemo(
    () => tiles.filter((t) => t.familyMemberId == familyMemberId),
    [tiles, familyMemberId]
  );

  const memberTests = React.useMemo(
    () => memberTiles.filter((t) => t.noteType === 'Test'),
    [memberTiles]
  );

  const memberRecordsByType = React.useMemo(() => {
    const by = {};
    for (const t of memberTiles) {
      if (!t || !t.noteType) continue;
      (by[t.noteType] = by[t.noteType] || []).push(t);
    }
    return by;
  }, [memberTiles]);

  const clinicalDataTiles = React.useMemo(() => {
    const result = [];
    (data?.diagnoses || []).forEach((d) => {
      result.push({
        _id: d._id || `disease-${d.id}`,
        noteType: 'Disease',
        familyMemberId,
        name: d.name,
        id: d.id,
        age: d.age,
        dateReported: d.dateReported,
        clinicallyConfirmed: d.clinicallyConfirmed,
        createdAt: d.dateReported || d.createdAt
      });
    });
    (data?.phenotypes || []).forEach((p) => {
      result.push({
        _id: p._id || `phenotype-${p.id}`,
        noteType: 'Phenotype',
        familyMemberId,
        name: p.name,
        id: p.id,
        dateRecorded: p.dateRecorded,
        recordedAge: p.recordedAge,
        clinicallyConfirmed: p.clinicallyConfirmed,
        createdAt: p.dateRecorded || p.createdAt
      });
    });
    (data?.genotypes || []).forEach((g) => {
      result.push({
        _id: g._id || `genotype-${g.id}`,
        noteType: 'Genotype',
        familyMemberId,
        name: g.name,
        id: g.id,
        dateRecorded: g.dateRecorded,
        recordedAge: g.recordedAge,
        clinicallyConfirmed: g.clinicallyConfirmed,
        carrierStatus: g.carrierStatus,
        createdAt: g.dateRecorded || g.createdAt
      });
    });
    return result;
  }, [data?.diagnoses, data?.phenotypes, data?.genotypes, familyMemberId]);

  // Reproductive records (Track B) — surfaced in Browse All Records alongside the clinical
  // notes so a clinician can scan all clinically relevant history in one place. Gated on the
  // repro-nodes flag; view-only here (edit via the Reproductive accordion / pedigree).
  const reproTiles = React.useMemo(() => {
    if (!reproConfig.ENABLE_REPRO_NODES) return [];
    return (data?.pregnancies || []).map((p) => ({
      _id: p._id || `pregnancy-${p.id}`,
      noteType: 'Pregnancy',
      familyMemberId,
      outcome: p.outcome,
      gestationalWeeks: p.gestationalWeeks,
      conceivedWith: p.conceivedWith,
      fetalSexKnown: p.fetalSexKnown,
      fetalSex: p.fetalSex,
      karyotype: p.karyotype,
      comments: p.comments,
      hidden: p.hidden,
      createdAt: p.createdAt
    }));
  }, [data?.pregnancies, familyMemberId]);

  const user_id = user?.user_id || user?._id;
  const [riskRecords, setRiskRecords] = React.useState([]);
  const fetchRiskRecords = React.useCallback(() => {
    if (!user_id) return;
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', user_id))
      .then(({ data: res }) => setRiskRecords(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, [user_id]);
  React.useEffect(() => {
    fetchRiskRecords();
  }, [fetchRiskRecords]);

  React.useEffect(() => {
    if (!isModalOpen && noteType === 'Risk Factor') fetchRiskRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const riskTiles = React.useMemo(() => {
    const RISK_LABELS = {
      gail: 'GAIL',
      canrisk: 'CanRisk',
      prs: 'PRS',
      questionnaire: 'Questionnaire'
    };

    const memberIds = [data?.proBandId, data?.familyMemberId]
      .filter(Boolean)
      .map(String);
    return riskRecords
      .filter(
        (r) =>
          memberIds.length === 0 || memberIds.includes(String(r.familyMemberId))
      )
      .map((r) => ({
        _id: r._id,
        noteType: 'Risk Score',
        familyMemberId,
        recordType: r.recordType,
        riskLabel: RISK_LABELS[r.recordType] || r.recordType,
        gailRiskScore: r.gailRiskScore,
        canriskResult: r.canriskResult,
        prsResult: r.prsResult,
        createdAt: r.createdAt
      }));
  }, [riskRecords, familyMemberId, data?.proBandId, data?.familyMemberId]);

  const patientName = `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
  const patientId = familyMemberId !== 'none' ? familyMemberId : '';

  const [isDisabled, setIsDisabled] = React.useState(false);
  const [newNote, setNewNote] = React.useState(false);
  const addClicked = async () => {
    setOpenModal(true);
    setNewNote(true);
    setIsDisabled(false);
  };

  const [selectedRecordType, setSelectedRecordType] = useState('');

  const handleRecordTypeSelect = (event) => {
    const value = event.target.value;
    if (value) {
      setNoteType(value);
      setOpenModal(true);
      setNewNote(true);
      setIsDisabled(false);
      setSelectedRecordType('');
    }
  };

  const handleChanges = (key) => {
    let replace = { ...filter };
    replace[key] = !replace[key];
    setFilter(replace);
  };

  const [noteTypes] = React.useState([
    'Address',
    'Appointment',
    'Clinical Note',
    'Consent',
    'Contact',
    'Process',
    'Referral',
    'Sample',
    'Test',
    'Treatment',
    'Tissue Pathology',
    'Risk Factor'
  ]);

  const handleClose = () => {
    setUnsavedChanges({ status: false, edit: false });
    setOpenModal(false);
    setIsEditing(false);
    setNewNote(false);
  };

  const handleNoteClose = (event) => {
    if (event?.exit) {
      handleClose();
    } else if (unsavedChanges.edit) {
      setUnsavedChanges((prev) => ({ ...prev, status: true }));
    } else handleClose();
  };

  const handleCancel = () => {
    setUnsavedChanges((prev) => ({ ...prev, status: false }));
  };

  const isProband = !!data?.proBandId;
  const getMemberKey = React.useCallback((member) => {
    return String(
      member?.familyMemberId ?? member?.proBandId ?? member?.pid ?? ''
    );
  }, []);

  const handleSuggestionsAccepted = React.useCallback(
    ({ phenotypes, genotypes, diagnoses }) => {
      if (!setMemberRecords || !(phenotypes || genotypes || diagnoses)) return;
      setMemberRecords((prev) => {
        let updatedMember = null;

        const updated = prev.map((member) => {
          const memberId =
            member.familyMemberId ?? member.proBandId ?? member.pid ?? '';

          if (memberId !== familyMemberId) return member;

          updatedMember = {
            ...member,
            ...(phenotypes && { phenotypes }),
            ...(genotypes && { genotypes }),
            ...(diagnoses && { diagnoses })
          };
          return updatedMember;
        });

        return updated;
      });
    },
    [setMemberRecords, familyMemberId]
  );

  const refreshPedigreeLabels = React.useCallback(async () => {
    if (!familyId || !setMemberRecords) return;

    try {
      const response = await axios.get(
        APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)
      );
      const { proband, families = [] } = response.data || {};
      const refreshedMembers = [proband, ...families].filter(Boolean);
      const refreshedMap = new Map(
        refreshedMembers.map((member) => [getMemberKey(member), member])
      );

      const fmt = theme?.dateFormat || 'MM/DD/YYYY';

      setMemberRecords((prevRecords) =>
        prevRecords.map((member) => {
          const refreshedMember = refreshedMap.get(getMemberKey(member));
          if (!refreshedMember) return member;
          const raw = refreshedMember.consultationDate;
          const consultationDate = Array.isArray(raw)
            ? raw.map((d) => moment(d).format(fmt)).join(', ')
            : raw || '';
          return {
            ...member,
            consultationDate,
            confirmedDiagnosis: refreshedMember.confirmedDiagnosis || '',
            tests: refreshedMember.tests || '',
            testRecords: refreshedMember.testRecords || [],
            testResults: refreshedMember.testResults || '',
            pedigreeText: refreshedMember.pedigreeText || ''
          };
        })
      );

      dispatchUpdate({
        type: EVENTS.SET_PEDIGREE_SELECTED_USER,
        value: {
          ...users,
          selectedUser: proband,
          family: families
        }
      });
    } catch (err) {
      console.error('Failed to refresh pedigree labels:', err);
    }
  }, [dispatchUpdate, familyId, getMemberKey, setMemberRecords, theme, users]);

  const forms = (type, model = {}) => {
    switch (type) {
      case 'Referral':
        return (
          <ReferralForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Appointment':
        return (
          <AppointmentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            refreshPedigreeLabels={refreshPedigreeLabels}
          />
        );
      case 'Contact':
        return (
          <ContactForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            data={data}
            memberTests={memberTests}
            memberRecordsByType={memberRecordsByType}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Consent':
        return (
          <ConsentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Sample':
        return (
          <SampleForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Test':
        return (
          <TestForm
            handleClose={handleClose}
            data={data}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            refreshPedigreeLabels={refreshPedigreeLabels}
          />
        );
      case 'Treatment':
        return (
          <TreatmentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Address':
        return (
          <AddressForm
            handleClose={handleClose}
            familyId={familyId}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Clinical Note':
        return (
          <ClinicalNotesForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onSuggestionsAccepted={handleSuggestionsAccepted}
          />
        );
      case 'Tissue Pathology':
        return (
          <TissuePathologyForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Process':
        return (
          <ProcessForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Risk Factor':
        return (
          <RiskFactorForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      default:
        return <></>;
    }
  };

  const fetchStaffMembers = () => {
    axios.get(`/clinical-notes/staff-members`).then((res) => {
      const parsedStaffMembers = res.data.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        userId: member._id
      }));
      setStaffMembers(parsedStaffMembers || []);
    });
  };

  React.useEffect(() => {
    fetchStaffMembers();
  }, []);

  const doFetch = React.useCallback(() => {
    setIsLoading(true);

    return axios
      .get(`/clinical-notes/all`, {
        params: { familyId }
      })
      .then((res) => {
        let edited = res.data;
        // Sort the notes based on the pinned property
        edited.sort(function (a) {
          if (a.pinned) return -1;
          return 1;
        });

        // Transform the data based on noteType
        const transformedData = edited.map((note) => {
          // Handle Consent type note - keep obtainedBy as string (same as Sample)
          if (note.noteType === 'Consent') {
            if (Array.isArray(note.obtainedBy) && note.obtainedBy.length > 0) {
              const s = note.obtainedBy[0];
              note.obtainedBy = `${s.firstName ?? ''} ${
                s.lastName ?? ''
              }`.trim();
            }
            // If already a string, keep it as is
          }
          if (note.noteType === 'Test') {
            note.requestedBy =
              Array.isArray(note.requestedBy) && note.requestedBy.length > 0
                ? note.requestedBy[0].userId
                : null;
          }
          if (note.noteType === 'Appointment') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;
          }
          if (note.noteType === 'Contact') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;

            note.requestChecking =
              Array.isArray(note.requestChecking) &&
              note.requestChecking.length > 0
                ? note.requestChecking[0].userId
                : null;
          }
          if (note.noteType === 'Tissue Pathology') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
            note.pathologyDoctor =
              Array.isArray(note.pathologyDoctor) &&
              note.pathologyDoctor.length > 0
                ? note.pathologyDoctor[0].userId
                : '';
          }
          if (note.noteType === 'Process') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
          }
          return note;
        });
        setTiles(transformedData);

        setIsLoading(false);
        // Auto-open a clinical record modal when a record ID is present in the URL
        if (familyMemberIdFromURL && !isOpenedModal.current) {
          const urlRecordMap = [
            { id: appointmentIdFromURL, noteType: 'Appointment' },
            { id: referralIdFromURL, noteType: 'Referral' },
            { id: contactIdFromURL, noteType: 'Contact' },
            { id: sampleIdFromURL, noteType: 'Sample' },
            { id: testIdFromURL, noteType: 'Test' },
            { id: clinicalNoteIdFromURL, noteType: 'Clinical Note' },
            { id: consentIdFromURL, noteType: 'Consent' }
          ];

          for (const { id, noteType: type } of urlRecordMap) {
            if (!id) continue;
            const record = transformedData.find(
              (data) =>
                data.familyMemberId === familyMemberIdFromURL && data._id === id
            );
            if (record) {
              setIsEditing(true);
              setCurrentModel(record);
              setNoteType(type);
              setOpenModal(true);
              isOpenedModal.current = true;
              break;
            }
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching clinical notes:', err);
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [
    familyId,
    familyMemberIdFromURL,
    appointmentIdFromURL,
    referralIdFromURL,
    contactIdFromURL,
    sampleIdFromURL,
    testIdFromURL,
    clinicalNoteIdFromURL,
    consentIdFromURL
  ]);

  React.useEffect(() => {
    // Only fetch when the modal is closed — avoids a race condition where an
    // in-flight fetch from modal-open completes *after* the post-save fetch
    // and overwrites tiles with stale (pre-save) data.
    if (isModalOpen) return;
    setTiles([]);
    doFetch();
  }, [isModalOpen, doFetch]);

  React.useEffect(() => {
    window.addEventListener('clinical-notes-refresh', doFetch);
    return () => window.removeEventListener('clinical-notes-refresh', doFetch);
  }, [doFetch]);

  const openTestRecord = React.useCallback((record) => {
    const normalized = { ...record };
    if (
      Array.isArray(normalized.requestedBy) &&
      normalized.requestedBy.length > 0
    ) {
      normalized.requestedBy = normalized.requestedBy[0].userId;
    }
    setIsEditing(true);
    setNewNote(false);
    setCurrentModel(normalized);
    setNoteType('Test');
    setIsDisabled(false);
    setOpenModal(true);
  }, []);

  const openSourceTestRecord = React.useCallback(
    async ({ testId } = {}) => {
      if (!testId) return false;
      const cached = tiles.find(
        (t) => t.noteType === 'Test' && String(t._id) === String(testId)
      );
      if (cached) {
        openTestRecord(cached);
        return true;
      }
      try {
        const { data: record } = await axios.get(
          `/clinical-notes/tests/${testId}`
        );
        if (record?._id) {
          openTestRecord(record);
          return true;
        }
      } catch (err) {
        console.error('Failed to load source test record', err);
      }
      return false;
    },
    [tiles, openTestRecord]
  );

  React.useEffect(() => {
    const handler = (e) => {
      const testId = e?.detail?.testId;
      if (!testId) return;
      openSourceTestRecord({ testId });
    };
    window.addEventListener('open-source-test-record', handler);
    return () => window.removeEventListener('open-source-test-record', handler);
  }, [openSourceTestRecord]);

  React.useEffect(() => {
    const handler = (e) => {
      const preselect = e?.detail?.model;
      setIsEditing(false);
      setCurrentModel(preselect ? { recordType: preselect } : {});
      setNoteType('Risk Factor');
      setNewNote(true);
      setIsDisabled(false);
      setOpenModal(true);
    };
    window.addEventListener('open-risk-factor-modal', handler);
    return () => window.removeEventListener('open-risk-factor-modal', handler);
  }, []);

  //Fix empty form selection values, shouldn't be necessary with radio
  React.useEffect(() => {
    if (noteType === '') setNoteType('Referral');
  }, [noteType]);

  const personSummary = React.useCallback(() => {
    let details = data;
    if (!newNote)
      details = deepCopy?.find(
        (d) => d.familyMemberId === currentModel.familyMemberId
      );
    return formatPersonSummary(details);
  }, [currentModel, newNote]);

  const renderTiles = () => {
    const MemberCards = [];
    const FamilyCards = [];
    const sorted = [...tiles, ...riskTiles].sort((a, b) => {
      const da = new Date(getRecordDate(a) || a.createdAt || 0);
      const db = new Date(getRecordDate(b) || b.createdAt || 0);
      return sortOrder === 'newest' ? db - da : da - db;
    });
    sorted.forEach((item) => {
      const tileType =
        item.noteType === 'Risk Score' && item.riskLabel
          ? 'Risk Factor'
          : item.noteType;
      const riskLabel =
        item.noteType === 'Risk Score' ? item.riskLabel : undefined;
      if (filter[item.noteType] && familyMemberId == item.familyMemberId)
        MemberCards.push(
          <TileCard
            model={item}
            key={item._id}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={deepCopy}
            staffMemberOptions={staffMemberOptions}
            onCustomClick={
              item.noteType === 'Risk Score'
                ? () => {
                    const orig = riskRecords.find((r) => r._id === item._id);
                    if (!orig) return;
                    setIsEditing(true);
                    setCurrentModel(orig);
                    setNoteType('Risk Factor');
                    setOpenModal(true);
                  }
                : undefined
            }
          />
        );

      if (item.showInFamily)
        FamilyCards.push(
          <TileCard
            model={item}
            key={item._id}
            showInFamily={item.showInFamily}
            highlightedPerson={data}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={deepCopy}
            staffMemberOptions={staffMemberOptions}
            setIsDisabled={setIsDisabled}
          />
        );
    });
    return { MemberCards, FamilyCards };
  };

  return (
    <div className="user-form">
      {familyMemberId !== 'none' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }} disabled={isLocked}>
            <Select
              data-va-anchor="pedigree.clinicalRecords.typePicker"
              value={selectedRecordType}
              onChange={handleRecordTypeSelect}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) => {
                if (selected === '') {
                  return (
                    <span style={{ color: '#999' }}>
                      Create New Clinical Record
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem disabled value="">
                <em>Create New Clinical Record</em>
              </MenuItem>
              {noteTypes
                // Address records hold patient PII — not creatable on an
                // anonymous pedigree (backend rejects the route too).
                .filter((item) => !(anonymousActive && item === 'Address'))
                .map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          <Tooltip title="Browse all records">
            <span>
              <IconButton
                ref={browseAllRef}
                size="small"
                onClick={() => setIsBrowserOpen(true)}
                aria-label="Browse all clinical records"
                disabled={tiles.length === 0}
              >
                <ManageSearchIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={sortOrder === 'newest' ? 'Oldest first' : 'Newest first'}
          >
            <IconButton
              size="small"
              onClick={() =>
                setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))
              }
              aria-label="Toggle sort order"
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FilterClinicalRecord filter={filter} setFilter={setFilter} />
        </Box>
        {isLoading ? (
          <Chip label="Loading" />
        ) : renderTiles().MemberCards.length === 0 ? (
          <Chip label="No Notes Found!" />
        ) : (
          <>
            {renderTiles().MemberCards.map((tile, iter) => (
              <Grid key={iter} size={12}>
                {tile}
              </Grid>
            ))}
          </>
        )}
      </Box>
      {!isLoading && (
        <Box sx={{ marginTop: 4 }}>
          <hr />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <FormLabel component="label" className="form-label">
            Family Clinical Records
          </FormLabel>
          <IconButton
            onClick={() =>
              modificationHistoryHandle(
                CONSTANTS.auditLogType['clinicalRecord'],
                filter
              )
            }
          >
            <HistoryIcon />
          </IconButton>
        </Box>

        {renderTiles().FamilyCards.map((tile, iter) => (
          <Grid key={iter} size={12}>
            {tile}
          </Grid>
        ))}

        {renderTiles().FamilyCards.length === 0 && (
          <Chip label={`No Notes Found!`} />
        )}
      </Box>
      <AcitonDialog
        open={unsavedChanges.status}
        title={'Unsaved Changes'}
        saveText="Discard changes"
        handleClose={handleCancel}
        onSubmit={handleClose}
      >
        You have unsaved changes that will be lost if you continue. Would you
        like to discard your changes and proceed anyway?
      </AcitonDialog>
      <CustomDialog
        maxWidth={
          noteType === 'Risk Factor' ? 'lg' : CLINICAL_RECORD_DIALOG_WIDTH
        }
        title={
          <ModalHeader
            title={isEditing ? `Edit ${noteType}` : `Create New ${noteType}`}
            personSummary={personSummary}
          />
        }
        allowBackgroundClose={true}
        open={isModalOpen}
        handleClose={handleNoteClose}
        minHeight="auto"
        onClose={true}
        footerAction={
          isEditing && currentModel?._id && modificationHistoryHandle ? (
            <IconButton
              size="small"
              title="Record history"
              onClick={() =>
                modificationHistoryHandle(
                  CONSTANTS.auditLogType.clinicalRecord,
                  {},
                  currentModel._id
                )
              }
            >
              <HistoryIcon />
            </IconButton>
          ) : null
        }
      >
        <div data-va-anchor="pedigree.clinicalRecords.dialog">
          {forms(noteType, currentModel)}
        </div>
      </CustomDialog>
      <RecordBrowserModal
        open={isBrowserOpen}
        onClose={() => {
          setIsBrowserOpen(false);
          browseAllRef.current?.focus();
        }}
        tiles={[
          ...memberTiles,
          ...clinicalDataTiles,
          ...riskTiles,
          ...reproTiles
        ]}
        isLoading={isLoading}
        patientName={patientName}
        patientId={patientId}
        patientDob={data?.dob}
        staffMemberOptions={staffMemberOptions}
      />
    </div>
  );
}

const FilterClinicalRecord = ({ filter, setFilter }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const allOptions = Object.keys(filter).sort((a, b) => a.localeCompare(b));
  const selectedValues = allOptions.filter((key) => filter[key]);
  const selectedText =
    selectedValues.length > 0 && selectedValues.length < allOptions.length
      ? `${selectedValues.length} selected`
      : '';

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (name) => {
    setFilter((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSelectAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = true;
    });
    setFilter(newFilter);
  };

  const handleClearAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = false;
    });
    setFilter(newFilter);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Filter clinical records"
        >
          <FilterListIcon />
        </IconButton>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {selectedText}
        </Box>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { p: 1, minWidth: 260 } }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Button onClick={handleSelectAll} size="small">
            Select All
          </Button>
          <Button onClick={handleClearAll} size="small">
            Clear All
          </Button>
        </Box>
        <MenuList disablePadding>
          {allOptions.map((name) => (
            <MenuItem
              key={name}
              onClick={() => handleToggle(name)}
              sx={{ py: 0.5 }}
            >
              <Checkbox checked={filter[name]} size="small" />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </div>
  );
};

export default ClinicalNotes;
